import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Modal,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import api, { subscriptionAPI } from '../services/api';
import ActivationModal from '../components/ActivationModal';
import { formatCN } from '../utils/dateFormat';

const { Title, Text } = Typography;

interface Plan {
  id: string;
  name: string;
  display_name: string;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  price_monthly: number;
  price_yearly: number;
  sort_order: number;
}

interface Usage {
  metric: string;
  count: number;
  limit: number;
}

interface ActiveSub {
  id: string;
  plan_name: string;
  plan_display_name: string;
  sort_order: number;
  status: string;
  current_period_start: string;
  current_period_end: string;
  is_effective: boolean;
}

const FEATURE_LABELS: Record<string, string> = {
  ai_assistant: 'AI智能助手',
  report_export: '报告导出',
  tdm_optimize: 'TDM剂量优化',
  multi_drug: '多药物支持',
  custom_models: '自定义模型',
  api_access: 'API 接口',
};

const METRIC_LABELS: Record<string, string> = {
  patients: '本月患者数',
  calculations_month: '本月计算次数',
  members: '机构成员',
  models: '模型数',
};

const LIMIT_LABELS: Record<string, string> = {
  max_patients_month: '患者/月',
  max_calculations_month: '计算/月',
  max_members: '成员',
  max_models: '模型',
};

const LIMIT_ORDER = ['max_calculations_month', 'max_patients_month', 'max_members', 'max_models'];

const PLAN_TONES: Record<string, { label: string; accent: string; surface: string; line: string; ink: string }> = {
  free: {
    label: '入门体验',
    accent: '#667085',
    surface: '#F8FAFC',
    line: '#D0D5DD',
    ink: '#344054',
  },
  basic: {
    label: '日常诊疗',
    accent: '#0E7490',
    surface: '#ECFEFF',
    line: '#A5F3FC',
    ink: '#155E75',
  },
  professional: {
    label: '高级优化',
    accent: '#0B2545',
    surface: '#F0F7FF',
    line: '#B9D7F6',
    ink: '#0B2545',
  },
  pro: {
    label: '高级优化',
    accent: '#0B2545',
    surface: '#F0F7FF',
    line: '#B9D7F6',
    ink: '#0B2545',
  },
  enterprise: {
    label: '机构级协作',
    accent: '#A77A1F',
    surface: '#FFFAEB',
    line: '#F2D78D',
    ink: '#5E420A',
  },
};

const DEFAULT_PLAN_TONE = {
  label: '扩展能力',
  accent: '#2563EB',
  surface: '#F5F8FF',
  line: '#CBD5E1',
  ink: '#1E3A8A',
};

const SUBSCRIPTION_STYLES = `
  .subscription-page {
    height: min(760px, calc(100vh - 176px));
    min-height: min(640px, calc(100vh - 176px));
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px 14px;
    border: 1px solid rgba(11, 37, 69, 0.07);
    border-radius: 18px;
    background:
      radial-gradient(circle at 9% 0%, rgba(14, 116, 144, 0.1), transparent 30%),
      radial-gradient(circle at 94% 6%, rgba(197, 165, 90, 0.13), transparent 26%),
      linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
  }

  .subscription-page__header,
  .subscription-page__overview {
    flex: 0 0 auto;
  }

  .subscription-page__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .subscription-page__eyebrow {
    margin-top: 2px;
    color: #667085;
    font-size: 12px;
    line-height: 1.4;
  }

  .subscription-page__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .subscription-page__overview {
    display: grid;
    grid-template-columns: minmax(250px, 0.7fr) minmax(0, 1.6fr);
    gap: 10px;
  }

  .subscription-status-panel,
  .subscription-usage-panel {
    min-width: 0;
    border: 1px solid rgba(11, 37, 69, 0.08);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
  }

  .subscription-status-panel {
    padding: 9px 12px;
  }

  .subscription-status-panel__label,
  .subscription-usage-panel__label {
    color: #667085;
    font-size: 12px;
    line-height: 1.3;
  }

  .subscription-status-panel__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    color: #0B2545;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
  }

  .subscription-status-panel__meta {
    margin-top: 5px;
    color: #667085;
    font-size: 12px;
    line-height: 1.45;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-status-panel__note {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    color: #1D4ED8;
    font-size: 12px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-usage-panel {
    display: grid;
    grid-template-columns: minmax(150px, 0.32fr) minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
  }

  .subscription-usage-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 0;
  }

  .subscription-usage-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 7px;
  }

  .subscription-usage-chip {
    min-width: 0;
    padding: 6px 8px;
    border: 1px solid #E6EDF4;
    border-radius: 12px;
    background: #FBFCFE;
  }

  .subscription-usage-chip__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  .subscription-usage-chip__label {
    min-width: 0;
    color: #667085;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-usage-chip__value {
    color: #0B2545;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  .subscription-plan-grid {
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }

  .subscription-plan-card.ant-card {
    height: 100%;
    min-height: 0;
    border-color: var(--plan-line) !important;
    background: linear-gradient(180deg, #FFFFFF 0%, var(--plan-surface) 100%);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.07);
    overflow: hidden;
  }

  .subscription-plan-card.ant-card::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: linear-gradient(90deg, var(--plan-accent), rgba(255, 255, 255, 0));
  }

  .subscription-plan-card--current.ant-card {
    border-width: 2px;
    box-shadow: 0 18px 34px rgba(11, 37, 69, 0.16);
  }

  .subscription-plan-card .ant-card-body {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 15px 17px;
  }

  .subscription-plan-card__badge {
    align-self: flex-start;
    max-width: 100%;
    margin-bottom: 8px;
    padding: 3px 9px;
    border: 1px solid var(--plan-line);
    border-radius: 999px;
    color: var(--plan-ink);
    background: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-plan-card__head {
    min-height: 76px;
  }

  .subscription-plan-card__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .subscription-plan-card__price {
    margin-top: 7px;
    color: #0B2545;
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
  }

  .subscription-plan-card__price span {
    margin-left: 3px;
    color: #667085;
    font-size: 12px;
    font-weight: 500;
  }

  .subscription-plan-card__subprice {
    margin-top: 6px;
    color: #667085;
    font-size: 12px;
    line-height: 1.25;
  }

  .subscription-plan-card__limits {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    margin: 9px 0 9px;
  }

  .subscription-plan-card__limit {
    min-width: 0;
    padding: 5px 8px;
    border: 1px solid rgba(11, 37, 69, 0.07);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.65);
  }

  .subscription-plan-card__limit-label {
    display: block;
    color: #667085;
    font-size: 11px;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-plan-card__limit-value {
    display: block;
    margin-top: 2px;
    color: #101828;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-plan-card__features {
    flex: 0 0 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-content: start;
    column-gap: 14px;
    row-gap: 0;
    padding: 5px 8px;
    border: 1px solid rgba(11, 37, 69, 0.07);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.52);
  }

  .subscription-plan-card__feature {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    min-height: 26px;
    padding: 4px 0;
    border-bottom: 1px solid rgba(11, 37, 69, 0.06);
    color: #344054;
    font-size: 12px;
    line-height: 1.35;
  }

  .subscription-plan-card__feature:nth-last-child(-n + 2) {
    border-bottom: none;
  }

  .subscription-plan-card__feature span:last-child {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subscription-plan-card__feature--disabled {
    color: #98A2B3;
  }

  .subscription-plan-card__check {
    color: var(--plan-accent);
    font-size: 12px;
  }

  .subscription-plan-card__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid #D0D5DD;
    flex: 0 0 auto;
  }

  .subscription-plan-card__button {
    flex: 0 0 auto;
    margin-top: auto;
    height: 36px;
    border-radius: 10px;
  }

  .subscription-plan-card__button.ant-btn-primary {
    background: var(--plan-accent);
    border-color: var(--plan-accent);
    box-shadow: 0 8px 16px rgba(11, 37, 69, 0.14);
  }

  @media (max-width: 960px) {
    .subscription-page {
      height: auto;
      min-height: 0;
      overflow: visible;
    }

    .subscription-page__overview,
    .subscription-plan-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .subscription-usage-panel {
      grid-template-columns: 1fr;
    }

    .subscription-plan-card.ant-card {
      min-height: 420px;
    }
  }

  @media (max-width: 720px) {
    .subscription-page {
      padding: 10px;
      border-radius: 14px;
    }

    .subscription-page__header {
      align-items: flex-start;
      flex-direction: column;
    }

    .subscription-page__actions {
      width: 100%;
      justify-content: flex-start;
    }

    .subscription-page__overview,
    .subscription-plan-grid,
    .subscription-usage-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const getPlanTone = (name: string) => PLAN_TONES[name] || DEFAULT_PLAN_TONE;

const formatLimitValue = (value: number) => {
  if (value === -1) return '无限';
  return value.toLocaleString('zh-CN');
};

const clampPercent = (count: number, limit: number) => {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((count / limit) * 100));
};

const daysUntil = (value?: string) => {
  if (!value) return null;
  const end = new Date(value);
  const now = new Date();
  return Math.ceil((end.getTime() - now.getTime()) / 86400000);
};

const getPlanLimitHighlights = (plan: Plan) =>
  LIMIT_ORDER
    .map((key) => ({ key, label: LIMIT_LABELS[key], value: plan.limits?.[key] }))
    .filter((item): item is { key: string; label: string; value: number } => item.value !== undefined)
    .slice(0, 4);

const getPlanFeatureEntries = (plan: Plan) => {
  const entries = Object.entries(plan.features || {});
  return [
    ...entries.filter(([, enabled]) => enabled),
    ...entries.filter(([, enabled]) => !enabled),
  ];
};

const Subscription: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<Usage[]>([]);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [allSubs, setAllSubs] = useState<ActiveSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [activationOpen, setActivationOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState<Plan | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await api.get('/api/subscriptions/usage');
      setUsage(res.data || []);
    } catch {
      // ignore
    }
  }, []);

  const fetchAllSubs = useCallback(async () => {
    try {
      const subs = await subscriptionAPI.getAll();
      setAllSubs(subs || []);
    } catch {
      // ignore
    }
  }, []);

  const refreshPlan = useCallback(async () => {
    await Promise.all([fetchUsage(), fetchAllSubs()]);
    try {
      const res = await api.get('/api/subscriptions/current');
      if (res.data?.plan_name) setCurrentPlan(res.data.plan_name);
    } catch {
      // ignore
    }
  }, [fetchAllSubs, fetchUsage]);

  useEffect(() => {
    Promise.all([
      api.get('/api/subscriptions/plans').then((r) => setPlans(r.data || [])),
      refreshPlan(),
    ])
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [refreshPlan]);

  const currentPlanObj = useMemo(
    () => plans.find((plan) => plan.name === currentPlan),
    [plans, currentPlan],
  );
  const currentSortOrder = currentPlanObj?.sort_order ?? 0;
  const effectiveSub = useMemo(
    () => allSubs.find((sub) => sub.is_effective),
    [allSubs],
  );
  const currentDaysLeft = daysUntil(effectiveSub?.current_period_end);
  const backupSubCount = Math.max(allSubs.length - (effectiveSub ? 1 : 0), 0);

  const handleUpgrade = (planName: string) => {
    const plan = plans.find((item) => item.name === planName);
    if (!plan) return;

    if (plan.sort_order < currentSortOrder) {
      Modal.confirm({
        title: '确认订阅更低级别套餐？',
        content: (
          <div>
            <p>
              您当前生效的套餐为 <Tag color="blue">{currentPlanObj?.display_name}</Tag>，
              选择的套餐 <Tag>{plan.display_name}</Tag> 级别更低。
            </p>
            <p>
              系统会保留全部有效订阅，并自动使用当前可用的最高级套餐权限；
              待高级套餐到期后，再自动切换到剩余的最高级套餐。
            </p>
          </div>
        ),
        okText: '继续订阅',
        cancelText: '取消',
        onOk: () => setUpgradeTarget(plan),
      });
      return;
    }

    setUpgradeTarget(plan);
  };

  const handleActivationCode = () => {
    setUpgradeTarget(null);
    setActivationOpen(true);
  };

  const handleOnlinePayment = () => {
    setUpgradeTarget(null);
    message.info('当前环境未开放在线支付，请使用激活码兑换或由后台手工处理。');
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div className="subscription-page">
      <style>{SUBSCRIPTION_STYLES}</style>

      <div className="subscription-page__header">
        <div>
          <Title level={4} style={{ margin: 0, color: '#0B2545' }}>套餐管理</Title>
          <div className="subscription-page__eyebrow">
            以当前机构权益、用量与可升级路径为核心，一屏完成比较与操作。
          </div>
        </div>
        <div className="subscription-page__actions">
          <Button icon={<ReloadOutlined />} onClick={refreshPlan}>
            刷新
          </Button>
          <Button type="primary" icon={<KeyOutlined />} onClick={() => setActivationOpen(true)}>
            激活码兑换
          </Button>
        </div>
      </div>

      <div className="subscription-page__overview">
        <div className="subscription-status-panel">
          <div className="subscription-status-panel__label">当前生效套餐</div>
          <div className="subscription-status-panel__title">
            <CrownOutlined style={{ color: '#C5A55A' }} />
            <span>{currentPlanObj?.display_name || currentPlan}</span>
            {currentSortOrder > 0 && <Tag color="blue">L{currentSortOrder}</Tag>}
          </div>
          <div className="subscription-status-panel__meta">
            {effectiveSub ? (
              <>
                有效期至 {formatCN(effectiveSub.current_period_end, 'YYYY-MM-DD')}
                {currentDaysLeft !== null && currentDaysLeft <= 7 && (
                  <Tag color="red" icon={<ClockCircleOutlined />} style={{ marginLeft: 6 }}>
                    即将到期
                  </Tag>
                )}
              </>
            ) : (
              '当前使用默认免费权益，可通过激活码或订阅升级。'
            )}
          </div>
          {backupSubCount > 0 && (
            <div className="subscription-status-panel__note">
              <InfoCircleOutlined />
              另有 {backupSubCount} 个备用订阅，到期后自动衔接最高级权益。
            </div>
          )}
        </div>

        <div className="subscription-usage-panel">
          <div className="subscription-usage-panel__head">
            <div>
              <div className="subscription-usage-panel__label">本月用量</div>
              <Text strong style={{ color: '#0B2545' }}>关键额度实时概览</Text>
            </div>
          </div>
          <div className="subscription-usage-grid">
            {usage.map((item) => (
              <div className="subscription-usage-chip" key={item.metric}>
                <div className="subscription-usage-chip__top">
                  <span className="subscription-usage-chip__label">
                    {METRIC_LABELS[item.metric] || item.metric}
                  </span>
                  <span className="subscription-usage-chip__value">
                    {item.limit === -1 ? `${item.count} / 无限` : `${item.count} / ${item.limit}`}
                  </span>
                </div>
                {item.limit === -1 ? (
                  <Progress percent={100} showInfo={false} size="small" status="success" />
                ) : (
                  <Progress
                    percent={clampPercent(item.count, item.limit)}
                    size="small"
                    showInfo={false}
                    status={item.count >= item.limit ? 'exception' : 'active'}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="subscription-plan-grid">
        {plans.map((plan) => {
          const tone = getPlanTone(plan.name);
          const isCurrent = plan.name === currentPlan;
          const isOwned = allSubs.some((item) => item.plan_name === plan.name);
          const isLower = plan.sort_order < currentSortOrder;
          const limitHighlights = getPlanLimitHighlights(plan);
          const featureEntries = getPlanFeatureEntries(plan);

          let buttonText = '立即订阅';
          if (isCurrent) buttonText = '当前生效';
          else if (isOwned) buttonText = '已拥有';
          else if (plan.name === 'enterprise') buttonText = '联系我们';
          else if (isLower) buttonText = '订阅此套餐';

          return (
            <Card
              key={plan.id}
              hoverable
              className={`subscription-plan-card ${isCurrent ? 'subscription-plan-card--current' : ''}`}
              style={{
                '--plan-accent': tone.accent,
                '--plan-surface': tone.surface,
                '--plan-line': tone.line,
                '--plan-ink': tone.ink,
              } as React.CSSProperties}
            >
              <div className="subscription-plan-card__badge">
                {isCurrent && (
                  <>
                    <CheckCircleFilled style={{ marginRight: 5, color: tone.accent }} />
                    当前生效
                  </>
                )}
                {!isCurrent && isOwned && (
                  <>
                    <CheckCircleFilled style={{ marginRight: 5, color: '#16A34A' }} />
                    已拥有（备用）
                  </>
                )}
                {!isCurrent && !isOwned && tone.label}
              </div>

              <div className="subscription-plan-card__head">
                <div className="subscription-plan-card__title">
                  <Title level={4} style={{ margin: 0, color: '#0B2545' }}>
                    {plan.display_name}
                  </Title>
                  {plan.name === 'enterprise' && <CrownOutlined style={{ fontSize: 21, color: '#C5A55A' }} />}
                </div>
                <div className="subscription-plan-card__price">
                  {plan.price_monthly > 0 ? (
                    <>
                      ¥{plan.price_monthly}
                      <span>/月</span>
                    </>
                  ) : plan.name === 'enterprise' ? (
                    <>联系销售</>
                  ) : (
                    <>免费</>
                  )}
                </div>
                <div className="subscription-plan-card__subprice">
                  {plan.price_yearly > 0
                    ? `年付 ¥${plan.price_yearly}`
                    : plan.name === 'enterprise'
                      ? '按机构需求定制开通'
                      : '永久免费'}
                </div>
              </div>

              <div className="subscription-plan-card__limits">
                {limitHighlights.length > 0 ? (
                  limitHighlights.map((item) => (
                    <div className="subscription-plan-card__limit" key={item.key}>
                      <span className="subscription-plan-card__limit-label">{item.label}</span>
                      <span className="subscription-plan-card__limit-value">
                        {formatLimitValue(item.value)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="subscription-plan-card__limit" style={{ gridColumn: '1 / -1' }}>
                    <span className="subscription-plan-card__limit-label">额度</span>
                    <span className="subscription-plan-card__limit-value">按后台配置开通</span>
                  </div>
                )}
              </div>

              <div className="subscription-plan-card__features">
                {featureEntries.map(([key, enabled]) => (
                  <div
                    className={`subscription-plan-card__feature ${
                      enabled ? '' : 'subscription-plan-card__feature--disabled'
                    }`}
                    key={key}
                  >
                    {enabled ? (
                      <CheckOutlined className="subscription-plan-card__check" />
                    ) : (
                      <span className="subscription-plan-card__dot" />
                    )}
                    <span>{FEATURE_LABELS[key] || key}</span>
                  </div>
                ))}
              </div>

              <Button
                className="subscription-plan-card__button"
                type={isCurrent || isOwned ? 'default' : 'primary'}
                block
                disabled={isCurrent || isOwned}
                href={plan.name === 'enterprise' ? 'mailto:support@precidose.ai' : undefined}
                onClick={plan.name === 'enterprise' ? undefined : () => handleUpgrade(plan.name)}
              >
                {buttonText}
              </Button>
            </Card>
          );
        })}
      </div>

      <Modal
        title={`${(upgradeTarget?.sort_order ?? 0) < currentSortOrder ? '订阅' : '升级到'} ${upgradeTarget?.display_name || ''}`}
        open={!!upgradeTarget}
        onCancel={() => setUpgradeTarget(null)}
        footer={null}
        width={420}
      >
        <div style={{ padding: '8px 0 4px' }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="当前生产环境仅保留后台支付配置，不开放前台在线支付。请使用激活码兑换；如需人工开通，请联系管理员。"
          />
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Button type="default" block size="large" onClick={handleOnlinePayment}>
              查看支付说明
            </Button>
            <Button type="primary" block size="large" icon={<KeyOutlined />} onClick={handleActivationCode}>
              激活码兑换
            </Button>
          </Space>
        </div>
      </Modal>

      <ActivationModal
        open={activationOpen}
        onClose={() => setActivationOpen(false)}
        onSuccess={refreshPlan}
      />
    </div>
  );
};

export default Subscription;
